import ReactDOM from 'react-dom';

import { CrossIcon } from 'assets/icons';
import { classnames } from 'helpers/utils';

import { useEffect } from 'react';
import styles from './modal.module.scss';

export enum ModalPosition {
  Center = 'center',
  Left = 'left',
  Right = 'right',
}

type ModalProps = {
  className?: string;
  contentClassName?: string;
  children: React.JSX.Element | React.JSX.Element[];
  disableManualClosing?: boolean;
  onClose?: () => void;
  title?: React.ReactNode;
  closeOnOutsideClick?: boolean;
  position?: ModalPosition;
  contentDataAttributes?: Record<string, string>;
};

const Modal = ({
  className = '',
  contentClassName = '',
  children,
  onClose,
  title,
  disableManualClosing = false,
  closeOnOutsideClick = false,
  position = ModalPosition.Center,
  contentDataAttributes,
}: ModalProps) => {
  const showCloseButton = () => (
    <button
      aria-label="Close"
      className={styles.headerIcon}
      type="button"
      onClick={onClose}
    >
      <CrossIcon />
    </button>
  );

  useEffect(() => {
    window.document.body.style.overflow = 'hidden';

    return () => {
      window.document.body.style.overflow = 'auto';
    };
  });

  const handleBackgroundClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnOutsideClick && onClose && e.target === e.currentTarget) {
      onClose();
    }
  };

  return ReactDOM.createPortal(
    <div
      className={classnames(
        styles[position],
        className,
        styles.modal,
        styles.openAnimation,
        styles.modalButtons,
      )}
    >
      <div
        role="button"
        tabIndex={0}
        className={styles.modalBackground}
        onClick={handleBackgroundClick}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && closeOnOutsideClick && onClose) {
            onClose();
          }
        }}
      />
      <div
        className={classnames(styles.modalContent, contentClassName)}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="presentation"
        {...contentDataAttributes}
      >
        <div className={styles.header}>
          <div className={classnames(styles.headerTitle)}>{title}</div>
          {!disableManualClosing && showCloseButton()}
        </div>
        {children}
      </div>
    </div>,
    document.getElementById('modal') as HTMLElement,
  );
};

export { Modal };
